import { Controller } from "@hotwired/stimulus";
import { reportError } from "@/utils/error_reporter";

// 무슨폰트 질문 폼: 이미지와 함께 "어디를 자를지"를 보낸다. 자르기는 서버가 한다(Post::AttachImage).
//
// 새 이미지: 이미지를 고르면 미리보기 자리에 자르기 영역이 뜨고, 브라우저 안에서 글자 위치를 찾아
//   (text_detection/text_detector.js) 상자를 글자 덩어리에 맞춘다. 사용자는 그 뒤에도 상자를 자유롭게
//   고칠 수 있고, 글자 찾기가 실패하면 전체 이미지가 잡힌 상태에서 직접 맞춘다.
// 다시 자르기(수정 화면): 보관된 원본과 지난번 영역을 띄워 영역만 다시 고른다. 파일은 다시 올리지 않는다.
//
// 제출할 때 원본 파일은 file input 그대로 가고, 영역은 formdata 이벤트에서 FormData 에 붙인다.
//   post[crop_rect][x|y|width|height]          사용자가 확정한 영역
//   post[auto_crop_rect][x|y|width|height|candidates]  글자 찾기가 처음 잡아 준 영역과 후보 수(새 이미지만)
// 값은 원본(EXIF 회전 반영 후) 가로·세로 대비 0~1 비율이다.
//
// Cropper.js 와 글자 찾기 모듈(모델·런타임 포함)은 이 폼에서만 동적으로 불러온다.
const MAX_FILE_SIZE = 10 * 1024 * 1024;

export default class extends Controller {
  static targets = ["input", "preview", "editor", "image", "status", "regionButton", "regionLabel", "recropButton"];
  static values = {
    // 새 질문 작성 화면에서만 켠다. 수정 화면은 이미지를 안 바꾸는 경우가 많아 고를 때 받는다.
    preload: Boolean,
    // 다시 자르기용 원본 주소(비공개 원본은 잠깐 열리는 주소)와 지난번 영역. 수정 화면에서만 온다.
    sourceUrl: String,
    cropRect: Object,
  };

  connect() {
    if (this.preloadValue) this.preload();
  }

  disconnect() {
    this.destroyCropper();
    this.revokeObjectUrl();
  }

  // 이미지를 고르기 전에 Cropper.js 와 글자 찾기 모델(약 7MB, 기기에 캐시)을 미리 받아 둔다.
  // 페이지 그리기와 다투지 않게 브라우저가 한가할 때 시작하고, 데이터 절약 모드면 받지 않는다.
  // 여기서의 실패는 보고하지 않는다. 이미지를 고르면 autoCrop 이 다시 시도하고 그때 실패하면 보고한다.
  preload() {
    if (navigator.connection?.saveData) return;

    const start = () => {
      import("cropperjs");
      import("@/text_detection/text_detector")
        .then(({ preloadTextDetector }) => preloadTextDetector())
        .catch((error) => {
          if (import.meta.env.DEV) console.warn("[image-crop] 글자 찾기 미리 받기 실패", error);
        });
    };
    if ("requestIdleCallback" in window) requestIdleCallback(start, { timeout: 2000 });
    else setTimeout(start, 500);
  }

  pick() {
    this.inputTarget.click();
  }

  // 새 이미지를 골랐을 때
  async load() {
    const file = this.inputTarget.files[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      alert("10MB 이하 크기의 파일만 올리실 수 있어요.");
      this.inputTarget.value = "";
      return;
    }

    this.revokeObjectUrl();
    this.objectUrl = URL.createObjectURL(file);
    const token = await this.openEditor("new", this.objectUrl);
    if (token) this.autoCrop(token);
  }

  // 수정 화면에서 보관된 원본으로 다시 자를 때
  async recrop() {
    if (!this.hasSourceUrlValue) return;

    const token = await this.openEditor("recrop", this.sourceUrlValue);
    if (!token) return;
    const loaded = await this.imageLoaded();
    if (token !== this.loadToken) return;
    if (!loaded) {
      // 서명 주소는 1시간 뒤 만료된다. 오래 열어 둔 화면이면 새로고침하면 된다.
      this.showStatus("원본 이미지를 불러오지 못했어요. 새로고침한 뒤 다시 시도해주세요.", true);
      reportError(new Error("다시 자르기 원본을 불러오지 못했습니다"), { tags: { feature: "post_image_recrop" } });
      return;
    }
    await this.ready;
    if (token !== this.loadToken) return;

    const rect = this.cropRectValue;
    if (rect && "width" in rect) this.cropper.setData(this.toPixels(rect));
    this.showStatus("사각형을 옮겨 다시 맞춰주세요. 저장하면 새로 잘린 이미지로 바뀌어요.");
  }

  // 자르기 영역을 띄운다. 다른 이미지를 다시 고르면 앞의 작업은 버린다(돌려준 token 으로 구분).
  async openEditor(mode, src) {
    const token = (this.loadToken = Symbol(mode));
    this.mode = mode;
    this.destroyCropper();
    this.resetRegions();
    this.imageTarget.src = src;

    this.previewTarget.classList.add("hidden");
    this.inputTarget.classList.add("hidden");
    if (this.hasRecropButtonTarget) this.recropButtonTarget.classList.add("hidden");
    this.editorTarget.classList.remove("hidden");

    const [{ default: Cropper }] = await Promise.all([
      import("cropperjs"),
      import("cropperjs/dist/cropper.css"),
    ]);
    if (token !== this.loadToken) return null;

    let markReady;
    this.ready = new Promise((resolve) => (markReady = resolve));
    this.cropper = new Cropper(this.imageTarget, {
      viewMode: 1,             // 자르기 상자가 이미지 밖으로 나가지 않게
      autoCropArea: 1,         // 글자를 못 찾으면 전체가 잡힌 상태로 둔다
      zoomable: false,         // 휠로 페이지를 내리다 이미지가 확대되는 일을 막는다
      movable: false,
      rotatable: false,
      // 사진의 회전 정보(EXIF)는 브라우저가 이미 반영해 그린다. Cropper 가 한 번 더 돌리면 좌표가
      // 브라우저·서버(vips autorot)와 어긋나므로 끈다.
      checkOrientation: false,
      // 원본은 다른 도메인(비공개 원본의 서명 주소)일 수 있다. Cropper 가 crossorigin 과 캐시 무효화
      // 쿼리를 붙이면 서명이 깨지므로 끈다. 이미지를 캔버스로 꺼내지 않으므로 필요 없다.
      checkCrossOrigin: false,
      background: false,
      guides: false,
      ready: () => markReady(),
      // 사용자가 상자를 직접 건드리면, 늦게 끝난 글자 찾기가 그 상자를 덮어쓰지 않는다.
      cropstart: () => { this.userAdjusted = true; },
    });
    return token;
  }

  async autoCrop(token) {
    if (!(await this.imageLoaded())) {
      if (token !== this.loadToken) return;
      // HEIC 처럼 브라우저가 못 여는 형식이다. 사용자 입력 문제라 보고하지 않는다.
      this.showStatus("이 이미지는 열 수 없어요. PNG나 JPG 파일로 올려주세요.", true);
      return;
    }
    this.showStatus("글자 위치를 찾고 있어요…");
    try {
      const { detectTextRegions } = await import("@/text_detection/text_detector");
      const [regions] = await Promise.all([detectTextRegions(this.imageTarget), this.ready]);
      if (token !== this.loadToken) return;

      this.regions = regions;
      if (regions.length === 0) {
        this.showStatus("글자를 찾지 못했어요. 사각형을 직접 맞춰주세요.");
        return;
      }
      if (!this.userAdjusted) this.applyRegion(0);
      this.regionButtonTarget.classList.remove("hidden");
      this.updateRegionUi();
    } catch (error) {
      if (token !== this.loadToken) return;
      this.showStatus("글자 위치를 자동으로 찾지 못했어요. 사각형을 직접 맞춰주세요.");
      reportError(error, {
        tags: { feature: "post_image_auto_crop" },
        extra: { fileType: this.inputTarget.files[0]?.type, fileSize: this.inputTarget.files[0]?.size },
      });
      if (import.meta.env.DEV) this.showStatus(`[개발] 자동 자르기 실패: ${error?.message || error}`, true);
    }
  }

  // 자르기 대상 이미지가 다 불러와지면 true, 못 불러오면 false.
  imageLoaded() {
    const img = this.imageTarget;
    if (img.complete) return Promise.resolve(img.naturalWidth > 0);
    return new Promise((resolve) => {
      img.addEventListener("load", () => resolve(true), { once: true });
      img.addEventListener("error", () => resolve(false), { once: true });
    });
  }

  // 글자 덩어리가 여럿이면 누를 때마다 다음 덩어리로, 하나면 그 덩어리로 다시 맞춘다.
  nextRegion() {
    if (!this.regions?.length) return;
    this.applyRegion((this.regionIndex + 1) % this.regions.length);
    this.updateRegionUi();
  }

  applyRegion(index) {
    this.regionIndex = index;
    this.cropper.setData(this.regions[index]);
  }

  updateRegionUi() {
    const count = this.regions.length;
    if (count > 1) {
      this.regionLabelTarget.textContent = `다른 글자 영역 (${this.regionIndex + 1}/${count})`;
      this.showStatus(`글자가 ${count}군데 있어요. 원하는 글자가 아니면 '다른 글자 영역'을 누르거나 사각형을 직접 맞춰주세요.`);
    } else {
      this.regionLabelTarget.textContent = "글자 영역으로 다시 맞추기";
      this.showStatus("글자 부분을 자동으로 잡았어요. 필요하면 사각형을 직접 고쳐주세요.");
    }
  }

  reset() {
    this.cropper?.reset();
  }

  // 폼이 제출될 때(form 의 formdata 이벤트) 영역을 FormData 에 붙인다. 원본 파일은 그대로 간다.
  // 자르기 영역이 아직 준비되지 않았으면 아무것도 붙이지 않는다 → 서버는 전체 이미지를 쓴다.
  appendCrop(event) {
    if (!this.cropper?.ready) return;

    const formData = event.formData;
    const appendRect = (name, rect) => {
      for (const [key, value] of Object.entries(this.toRatio(rect))) formData.append(`post[${name}][${key}]`, value);
    };
    appendRect("crop_rect", this.cropper.getData());
    if (this.mode === "new" && this.regions.length) {
      appendRect("auto_crop_rect", this.regions[0]);
      formData.append("post[auto_crop_rect][candidates]", this.regions.length);
    }
  }

  // Cropper·글자 찾기 좌표(원본 픽셀) → 0~1 비율
  toRatio({ x, y, width, height }) {
    const { naturalWidth: w, naturalHeight: h } = this.cropper.getImageData();
    const clamp = (v) => Math.min(1, Math.max(0, v));
    const rx = clamp(x / w), ry = clamp(y / h);
    return { x: rx, y: ry, width: Math.min(1 - rx, width / w), height: Math.min(1 - ry, height / h) };
  }

  toPixels({ x, y, width, height }) {
    const { naturalWidth: w, naturalHeight: h } = this.cropper.getImageData();
    return { x: x * w, y: y * h, width: width * w, height: height * h };
  }

  showStatus(message, isError = false) {
    this.statusTarget.textContent = message;
    this.statusTarget.classList.toggle("text-red-600", isError);
  }

  resetRegions() {
    this.regions = [];
    this.regionIndex = 0;
    this.userAdjusted = false;
    this.regionButtonTarget.classList.add("hidden");
    this.showStatus("");
  }

  destroyCropper() {
    this.cropper?.destroy();
    this.cropper = null;
  }

  revokeObjectUrl() {
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
    this.objectUrl = null;
  }
}
